import React, { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNostr } from '@/hooks/useNostr';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useQuery } from '@tanstack/react-query';
import { Tweet } from '@/components/tweet/Tweet';
import { ComposeBox } from '@/components/tweet/ComposeBox';
import { useTrustGraph } from '@/hooks/useTrustGraph';
import { filterFeedEvents, sortFeedByTrust } from '@/lib/feedFilters';
import { FollowPacks } from '@/components/discovery/FollowPacks';
import { useTrustSubscriptions } from '@/hooks/useNip85Trust';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ImageViewer } from '@/components/ui/ImageViewer';

export function Index() {
  useSeoMeta({
    title: 'Home / Twittstr',
    description: 'Nostr client replicating Twitter / X layout with real-time Decentralized Nostr feed under the Twittstr brand.',
  });

  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const [activeTab, setActiveTab] = useState<'for-you' | 'following'>('for-you');
  const [viewerSrc, setViewerSrc] = useState<string | null>(null);

  // Query global For You feed (kind 1 text notes)
  const { data: forYouNotes = [], isLoading: isForYouLoading, refetch: refetchForYou, isFetching: isForYouFetching } = useQuery({
    queryKey: ['for-you-feed'],
    queryFn: async () => {
      if (!nostr) return [];
      // Grab standard global posts
      return nostr.query([{ kinds: [1], limit: 40 }]);
    },
    enabled: !!nostr,
  });

  // Query Following contacts' feed
  const { data: contacts = [] } = useQuery({
    queryKey: ['my-contacts', user?.pubkey],
    queryFn: async () => {
      if (!nostr || !user) return [];
      // Fetch user kind-3 contact list
      const events = await nostr.query([{ kinds: [3], authors: [user.pubkey], limit: 1 }]);
      if (events[0]) {
        return events[0].tags.filter(t => t[0] === 'p').map(t => t[1]);
      }
      return [];
    },
    enabled: !!nostr && !!user,
  });

  const { data: followingNotes = [], isLoading: isFollowingLoading, refetch: refetchFollowing, isFetching: isFollowingFetching } = useQuery({
    queryKey: ['following-feed', contacts],
    queryFn: async () => {
      if (!nostr) return [];
      if (contacts.length === 0) return [];
      // Fetch kind 1 from followees
      return nostr.query([{ kinds: [1], authors: contacts, limit: 30 }]);
    },
    enabled: !!nostr && contacts.length > 0,
  });

  // Trust graph for spam filtering (distance-based)
  const { trustGraph, getDistance, shouldShowInGlobalFeed, isLoading: trustLoading } = useTrustGraph();

  // NIP-85 Trust/Mute list subscriptions
  const { trustedPubkeys: nip85Trusted, mutedPubkeys: nip85Muted } = useTrustSubscriptions();

  // Muted pubkeys and words (from localStorage + NIP-85 subscriptions)
  const manuallyMutedPubkeys = JSON.parse(localStorage.getItem('muted-pubkeys') || '[]') as string[];
  const mutedPubkeys = new Set<string>([...manuallyMutedPubkeys, ...Array.from(nip85Muted)]);
  const mutedWords: string[] = JSON.parse(localStorage.getItem('muted-words') || '[]');
  const contentWarningPolicy = (localStorage.getItem('content-warning-policy') || 'blur') as 'blur' | 'show' | 'hide';
  const preferredLanguage = localStorage.getItem('preferred-language') || 'all';

  // Apply spam filtering and trust-based sorting to For You feed
  const rawNotes = activeTab === 'for-you' ? forYouNotes : followingNotes;
  
  // Boost NIP-85 trusted accounts to at least Distance 2 in the trust graph
  const enhancedTrustGraph = trustGraph ? {
    getDistance: (pubkey: string) => {
      if (trustGraph.distance1.has(pubkey)) return 1;
      if (nip85Trusted.has(pubkey) || trustGraph.distance2.has(pubkey)) return 2;
      return trustGraph.getDistance(pubkey);
    },
    shouldShowInGlobalFeed: (pubkey: string) => {
      if (trustGraph.distance1.has(pubkey)) return { show: true, weight: 1.0 };
      if (nip85Trusted.has(pubkey) || trustGraph.distance2.has(pubkey)) return { show: true, weight: 0.5 };
      return { show: false, weight: 0 };
    },
  } : undefined;

  const filteredNotes = filterFeedEvents(rawNotes, {
    mutedPubkeys,
    mutedWords,
    contentWarningPolicy,
    preferredLanguage,
    trustGraph: enhancedTrustGraph,
    hideDistance3Plus: activeTab === 'for-you' && !trustLoading,
  });

  // Sort by trust distance (D1 first, D2 second, D3+ last)
  const currentNotes = enhancedTrustGraph && !trustLoading
    ? sortFeedByTrust(filteredNotes, (pubkey) => enhancedTrustGraph.getDistance(pubkey))
    : filteredNotes;

  const isLoading = activeTab === 'for-you' ? isForYouLoading : isFollowingLoading;
  const isFetching = activeTab === 'for-you' ? isForYouFetching : isFollowingFetching;

  // Handler for manual refreshes
  const handleRefresh = () => {
    if (activeTab === 'for-you') {
      refetchForYou();
    } else {
      refetchFollowing();
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-background">
      {/* Sticky Dual Feed Header (For You / Following) */}
      <header className="sticky top-0 bg-background/80 backdrop-blur-md z-30 border-b border-border flex flex-col pt-1.5 pb-0">
        <div className="flex md:hidden items-center justify-between px-4 pb-2 border-b border-border/40">
          <span className="font-extrabold text-lg flex items-center gap-1.5">
            Home <Sparkles className="size-4 text-sky-500 fill-sky-500" />
          </span>
          <button 
            disabled={isFetching}
            onClick={handleRefresh} 
            className="p-2 hover:bg-secondary rounded-full cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={`size-4 ${isFetching ? 'animate-spin text-sky-500' : ''}`} />
          </button>
        </div>
        
        <Tabs defaultValue="for-you" className="w-full" onValueChange={(val) => setActiveTab(val as any)}>
          <TabsList className="w-full flex h-13 bg-transparent p-0 rounded-none border-b-0 divide-x-0 relative justify-center">
            <TabsTrigger 
              value="for-you" 
              className="flex-1 h-full text-[15px] font-bold rounded-none border-b-4 border-transparent data-[state=active]:border-sky-500 data-[state=active]:text-foreground text-muted-foreground/80 cursor-pointer hover:bg-secondary/20 transition-all transition-all"
            >
              For you
            </TabsTrigger>
            <TabsTrigger 
              value="following" 
              className="flex-1 h-full text-[15px] font-bold rounded-none border-b-4 border-transparent data-[state=active]:border-sky-500 data-[state=active]:text-foreground text-muted-foreground/80 cursor-pointer hover:bg-secondary/20 transition-all transition-all"
            >
              Following
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </header>

      {/* Desktop Inline Compose tweet box */}
      {user && (
        <div className="hidden md:block p-4 border-b border-border">
          <ComposeBox />
        </div>
      )}

      {/* Follow Packs - Show for users with 0 follows or as onboarding suggestion */}
      {user && contacts.length === 0 && (
        <div className="border-b border-border">
          <div className="p-4 pb-2">
            <p className="text-xs text-muted-foreground leading-normal">
              You're not following anyone yet. Pick a pack below to get started, or find people on the Explore tab.
            </p>
          </div>
          <FollowPacks />
        </div>
      )}

      {/* Main Feed Content List */}
      <div className="flex flex-col w-full divide-y divide-border">
        {isLoading ? (
          // Loading Skeletons
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="p-4 flex gap-3 border-b border-border animate-pulse">
              <Skeleton className="size-10 rounded-full shrink-0" />
              <div className="flex-1 flex flex-col gap-2">
                <div className="flex gap-2">
                  <Skeleton className="h-4 w-28 rounded-md" />
                  <Skeleton className="h-4 w-16 rounded-md" />
                </div>
                <Skeleton className="h-4 w-full rounded-md" />
                <Skeleton className="h-4 w-3/4 rounded-md animate-delay-100" />
                <Skeleton className="h-28 w-full rounded-2xl mt-1" />
              </div>
            </div>
          ))
        ) : currentNotes.length > 0 ? (
          currentNotes.map((event) => (
            <Tweet key={event.id} event={event} onImageClick={(url) => setViewerSrc(url)} />
          ))
        ) : (
          // Empty state fallback card
          <div className="p-8 text-center flex flex-col items-center justify-center gap-3">
            <Card className="border-dashed bg-secondary/10 w-full max-w-sm rounded-2xl py-8">
              <CardContent className="flex flex-col items-center justify-center text-center gap-3 p-4">
                <p className="font-extrabold text-[15px]">Welcome to the Conversation</p>
                <p className="text-sm text-muted-foreground leading-normal">
                  {activeTab === 'following' && !user
                    ? 'Log in to discover and sync custom feeds of who you follow!'
                    : activeTab === 'following'
                    ? 'No posts from your network yet. Find people in the Explore page and follow them!'
                    : 'Searching for Nostr global posts... Make sure your relay connections are active.'}
                </p>
                {activeTab === 'following' && !user && (
                  <Button className="rounded-full bg-sky-500 hover:bg-sky-600 font-bold" onClick={() => window.location.href = '/settings'}>
                    Go to Settings
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Image viewer lightbox */}
      <ImageViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />
    </div>
  );
}

export default Index;
